// Single chokepoint for analytics events.
// - Today: forwards to gtag (GA4). Respects Consent Mode v2 (gtag honours it).
// - Tomorrow: when you stand up your own ingest endpoint, swap track() body
//   for a fetch to that endpoint with a localStorage queue fallback. Every
//   call site already uses the typed helpers below, so no churn needed.
//
// Naming convention: snake_case for event names, snake_case for param keys.
// Keep parameter values short and PII-free.

import { reportError } from './errorReporter';

const isBrowser = typeof window !== 'undefined';

const safe = (fn) => {
  try {
    return fn();
  } catch (e) {
    reportError(e, { source: 'analytics' });
    return undefined;
  }
};

const gtag = (...args) => {
  if (!isBrowser || typeof window.gtag !== 'function') return;
  safe(() => window.gtag(...args));
};

// Low-level event dispatcher. Use the named helpers below in 99% of cases.
export const track = (eventName, params = {}) => {
  gtag('event', eventName, {
    transport_type: 'beacon',
    ...params,
  });
};

// ---- Typed helpers (the public API) ------------------------------------

export const trackCtaClick = ({ label, location, route, ...extra }) => {
  track('cta_click', {
    cta_label: label,
    cta_location: location,
    page_path: route || (isBrowser ? window.location.pathname : undefined),
    ...extra,
  });
};

export const trackOutboundClick = ({ href, label }) => {
  track('outbound_click', {
    link_url: href,
    link_label: label,
    page_path: isBrowser ? window.location.pathname : undefined,
    // Per GA recommended schema:
    outbound: true,
  });
};

export const trackFormEvent = (stage, { formName, ...extra }) => {
  // stage: 'attempt' | 'success' | 'failure' | 'validation_error' | 'field_blur_empty'
  track(`form_submit_${stage}`, {
    form_name: formName,
    page_path: isBrowser ? window.location.pathname : undefined,
    ...extra,
  });
};

export const trackPageView = (path, title) => {
  track('page_view', {
    page_path: path,
    page_title: title,
    page_location: isBrowser ? window.location.href : undefined,
  });
};

export const trackScrollDepth = (depthPercent) => {
  track('scroll_depth', {
    percent_scrolled: depthPercent,
    page_path: isBrowser ? window.location.pathname : undefined,
  });
};

export const trackTimeOnPage = (seconds) => {
  track('time_on_page', {
    seconds,
    page_path: isBrowser ? window.location.pathname : undefined,
  });
};

export const trackNotFound = (failedPath, referrer) => {
  track('page_not_found', {
    failed_path: failedPath,
    referrer,
  });
};

export const trackSearch = (query, resultCount) => {
  track('search', {
    search_term: query,
    result_count: resultCount,
  });
};

export const trackPerf = (metricName, value, extra = {}) => {
  // Used by web-vitals reporter and long-task observer.
  track(metricName, {
    value: Math.round(value),
    ...extra,
    non_interaction: true,
  });
};
